import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import { I18nService, TranslatePipe, injectTranslate } from '@core/i18n-legacy';
import { SkillsImportDataFacade } from '@core/tellent-skills-logic';
import { SkillsImport } from '@core/tellent-skills-types';
import { StepperComponent, StepperStep, UploadFileBoxComponent } from '@core/tellent-ui';
import {
  ButtonComponent,
  CheckboxComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  HeadingComponent,
  IconComponent,
  NotificationsController,
  SpinnerComponent,
  TextareaComponent,
} from '@core/user-interface-legacy';
import { MaxFileSize } from '@core/utils-legacy';

@Component({
  selector: 'tlc-import-skills-upload-file-dialog',
  templateUrl: './import-skills-upload-file-dialog.component.html',
  styleUrl: './import-skills-upload-file-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    CheckboxComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    HeadingComponent,
    IconComponent,
    InfoboxComponent,
    ReactiveFormsModule,
    SpinnerComponent,
    StepperComponent,
    TextareaComponent,
    UploadFileBoxComponent,
    TranslatePipe,
  ],
})
export class ImportSkillsUploadFileDialogComponent implements DialogComponentI<void, SkillsImport> {
  private readonly t = injectTranslate();
  private readonly i18n = inject(I18nService);
  private readonly dialogRef = inject(DialogRef);
  private readonly notificationsController = inject(NotificationsController);
  private readonly destroyRef = inject(DestroyRef);
  private readonly skillsImportDataFacade = inject(SkillsImportDataFacade);

  protected readonly accept =
    'application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  protected readonly maxFileSize = MaxFileSize.File5MB;
  protected readonly maxInstructionsLength = 1000;

  protected readonly isUploading = signal(false);
  protected readonly uploadedFiles = signal<File[] | null>(null);
  protected readonly skillsImport = signal<SkillsImport | null>(null);

  protected readonly fileContentForm = new FormGroup({
    categories: new FormControl(false),
    positionsAssociated: new FormControl(false),
    proficiencyDescriptions: new FormControl(false),
    proficiencyLevels: new FormControl(false),
    uniqueNameDescription: new FormControl(false),
  });

  protected readonly additionalInstructions = new FormControl(
    '',
    Validators.maxLength(this.maxInstructionsLength),
  );

  protected readonly additionalInstructionsStatus = toSignal(
    this.additionalInstructions.statusChanges,
    {
      initialValue: this.additionalInstructions.status,
    },
  );

  protected readonly isContinueDisabled = computed(
    () =>
      this.isUploading() ||
      !this.skillsImport() ||
      this.additionalInstructionsStatus() === 'INVALID',
  );

  protected readonly fileContentOptions = [
    {
      control: this.fileContentForm.controls.uniqueNameDescription,
      label: this.t(
        'tlc.positions.import_skills_flow.upload_file.additional_instructions.file_content.options.unique_name_description',
      ),
    },
    {
      control: this.fileContentForm.controls.proficiencyLevels,
      label: this.t(
        'tlc.positions.import_skills_flow.upload_file.additional_instructions.file_content.options.proficiency_levels',
      ),
    },
    {
      control: this.fileContentForm.controls.proficiencyDescriptions,
      label: this.t(
        'tlc.positions.import_skills_flow.upload_file.additional_instructions.file_content.options.proficiency_descriptions',
      ),
    },
    {
      control: this.fileContentForm.controls.positionsAssociated,
      label: this.t(
        'tlc.positions.import_skills_flow.upload_file.additional_instructions.file_content.options.positions_associated',
      ),
    },
    {
      control: this.fileContentForm.controls.categories,
      label: this.t(
        'tlc.positions.import_skills_flow.upload_file.additional_instructions.file_content.options.categories',
      ),
    },
  ];

  protected readonly steps: StepperStep[] = [
    {
      id: 'upload_file',
      label: this.t('tlc.positions.import_skills_flow.stepper.upload_file'),
      active: true,
      completed: false,
    },
    {
      id: 'preview_skills',
      label: this.t('tlc.positions.import_skills_flow.stepper.preview_skills'),
      active: false,
      completed: false,
    },
    {
      id: 'apply_changes',
      label: this.t('tlc.positions.import_skills_flow.stepper.apply_changes'),
      active: false,
      completed: false,
    },
  ];

  public setDialogData(_data: void, _triggerUpdate: boolean): void {
    //
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public continue(): void {
    const skillsImport = this.skillsImport();
    if (!skillsImport) return;

    this.dialogRef.close({ ...skillsImport, userPrompt: this.buildUserPrompt() });
  }

  protected onFilesSelected(files: File[]): void {
    this.uploadedFiles.set(files);
    this.skillsImport.set(null);
    this.isUploading.set(true);

    this.skillsImportDataFacade
      .createImport({ files })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: skillsImport => {
          this.skillsImport.set(skillsImport);
          this.isUploading.set(false);
        },
        error: () => {
          this.uploadedFiles.set(null);
          this.isUploading.set(false);
          this.notificationsController.error({
            title: this.i18n.translate(
              'tlc.positions.import_skills_flow.upload_file.error_upload.title',
            ),
            message: this.i18n.translate(
              'tlc.positions.import_skills_flow.upload_file.error_upload.description',
            ),
          });
        },
      });
  }

  protected clearFiles(): void {
    this.uploadedFiles.set(null);
    this.skillsImport.set(null);
    this.fileContentForm.reset();
    this.additionalInstructions.reset();
  }

  protected onValidationError({ typeError }: { typeError: boolean; sizeError: boolean }): void {
    this.notificationsController.error({
      title: this.i18n.translate(
        typeError
          ? 'tlc.positions.import_skills_flow.upload_file.error_file_type.title'
          : 'tlc.positions.import_skills_flow.upload_file.error_file_size.title',
      ),
      message: this.i18n.translate(
        typeError
          ? 'tlc.positions.import_skills_flow.upload_file.error_file_type.description'
          : 'tlc.positions.import_skills_flow.upload_file.error_file_size.description',
      ),
    });
  }

  private buildUserPrompt(): string | null {
    const parts: string[] = this.fileContentOptions
      .filter(opt => opt.control.value)
      .map(opt => opt.label);
    const instructions = this.additionalInstructions.value?.trim();
    if (instructions) parts.push(instructions);
    return parts.length > 0 ? parts.join('\n') : null;
  }
}
